import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { NavLink, useLocation } from "react-router-dom";
import {
  IconLayout,
  IconSpeakerphone,
  IconFolder,
  IconListCheck,
  IconCornerUpLeftDouble,
  IconSend,
  IconDroplet,
  IconBolt,
  IconMail,
  IconUser,
  IconClipboardList,
  IconChartDonut,
  IconCalendarEvent,
  IconTicket,
  IconFileAnalytics,
  IconChevronDown,
} from "@tabler/icons";
import ShopifySvg from "../../../assets/svg/ShopifySvg";
import IconTags from "../../Layout/IconTags";
import IconPlaylistAdd from "../../Layout/IconPlaylistAdd";
import { hasPlatformFeature } from "../../../utils";

const Navigation = ({ siteId }: any) => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const location = useLocation();

  const navigationItems = [
    ...(hasPlatformFeature("dashboard")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.home"
                defaultMessage="Home"
              />
            ),
            href: `/sites/${siteId}/dashboard`,
            icon: <IconLayout stroke={1.5} size={24} />,
          },
        ]
      : []),
    {
      label: (
        <FormattedMessage
          id="main.sidebar.campaigns.title"
          defaultMessage="Campaigns"
        />
      ),
      href: `/sites/${siteId}/campaigns`,
      icon: <IconSpeakerphone stroke={1.5} size={24} />,
      hasDropdown: true,
      subMenu: [
        {
          label: (
            <FormattedMessage
              id="main.sidebar.campaigns.all"
              defaultMessage="ALL"
            />
          ),
          href: `/sites/${siteId}/campaigns`,
          type: "all",
          icon: <IconFolder stroke={1.5} size={20} />,
        },
        ...(hasPlatformFeature("widgets")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.forms"
                    defaultMessage="Forms & popups"
                  />
                ),
                href: `/sites/${siteId}/campaigns?type=widget`,
                type: "widget",
                icon: <IconListCheck stroke={1.5} size={20} />,
              },
            ]
          : []),
        ...(window?.isShopify && hasPlatformFeature("shopify_pages")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.shopify_pages"
                    defaultMessage="Shopify Pages"
                  />
                ),
                type: "shopify_page",
                href: `/sites/${siteId}/campaigns?type=shopify_page`,
                icon: <ShopifySvg size="20px" hasCustomStyles={true} />,
              },
            ]
          : []),
        ...(hasPlatformFeature("landing_pages")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.landing_pages"
                    defaultMessage="Landing Pages"
                  />
                ),
                type: "landing_page",
                href: `/sites/${siteId}/campaigns?type=landing_page`,
                icon: <IconLayout stroke={1.5} size={20} />,
              },
            ]
          : []),
        ...(hasPlatformFeature("autoresponders")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.auto_responders"
                    defaultMessage="Autoresponders"
                  />
                ),
                type: "welcome_email",
                href: `/sites/${siteId}/campaigns?type=welcome_email`,
                icon: <IconCornerUpLeftDouble stroke={1.5} size={20} />,
              },
            ]
          : []),
        ...(hasPlatformFeature("broadcasts")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.broadcasts"
                    defaultMessage="Broadcasts"
                  />
                ),
                href: `/sites/${siteId}/campaigns?type=broadcast_email`,
                type: "broadcast_email",
                icon: <IconSend stroke={1.25} size={20} />,
              },
            ]
          : []),
        ...(hasPlatformFeature("drips")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.drips"
                    defaultMessage="Drip Sequences"
                  />
                ),
                type: "drip_campaign",
                href: `/sites/${siteId}/campaigns?type=drip_campaign`,
                icon: <IconDroplet stroke={1.5} size={20} />,
              },
            ]
          : []),
        ...(hasPlatformFeature("automations")
          ? [
              {
                label: (
                  <FormattedMessage
                    id="main.sidebar.campaigns.automations"
                    defaultMessage="Automations"
                  />
                ),
                type: "automation",
                href: `/sites/${siteId}/campaigns?type=automation`,
                icon: <IconBolt stroke={1.5} size={20} />,
              },
            ]
          : []),
      ],
    },
    ...(hasPlatformFeature("campaigns", "email_template_library")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.emailLibrary"
                defaultMessage="Email library"
              />
            ),
            href: `/sites/${siteId}/email-library`,
            icon: <IconMail stroke={1.5} size={24} />,
          },
        ]
      : []),
    ...(hasPlatformFeature("subscribers")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.contacts"
                defaultMessage="Contacts"
              />
            ),
            href: `/sites/${siteId}/contacts`,
            icon: <IconUser stroke={1.5} size={24} />,
            hasDropdown: true,
            subMenu: [
              ...(hasPlatformFeature("subscribers", "lists")
                ? [
                    {
                      label: (
                        <FormattedMessage
                          id="page.contacts.dropdowns.audience-settings.options.list"
                          defaultMessage="Lists"
                        />
                      ),
                      href: `/sites/${siteId}/lists`,
                      icon: <IconClipboardList stroke={1.5} size={20} />,
                    },
                  ]
                : []),
              ...(hasPlatformFeature("subscribers", "tags")
                ? [
                    {
                      label: (
                        <FormattedMessage
                          id="page.contacts.dropdowns.audience-settings.options.tags"
                          defaultMessage="Tags"
                        />
                      ),
                      href: `/sites/${siteId}/tags`,
                      icon: <IconTags />,
                    },
                  ]
                : []),
              ...(hasPlatformFeature("subscribers", "custom_fields")
                ? [
                    {
                      label: (
                        <FormattedMessage
                          id="page.contacts.dropdowns.audience-settings.options.custom-fields"
                          defaultMessage="Custom Fields"
                        />
                      ),
                      href: `/sites/${siteId}/custom_fields`,
                      icon: <IconPlaylistAdd className="custom_fields" size={20} />,
                    },
                  ]
                : []),
              ...(hasPlatformFeature("subscribers", "advanced_filters")
                ? [
                    {
                      label: (
                        <FormattedMessage
                          id="page.contacts.dropdowns.audience-settings.options.segments"
                          defaultMessage="Segments"
                        />
                      ),
                      href: `/sites/${siteId}/segments`,
                      icon: <IconChartDonut stroke={1.5} size={20} />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasPlatformFeature("hotel_reservations")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.reservations"
                defaultMessage="Reservations"
              />
            ),
            href: `/sites/${siteId}/reservations`,
            icon: <IconCalendarEvent stroke={1.5} size={24} />,
          },
        ]
      : []),
    ...((window.isShopify ||
      (window.isWix && window.isBetaUser) ||
      window.isAvb ||
      false) &&
    hasPlatformFeature("coupons")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.coupons"
                defaultMessage="Coupons"
              />
            ),
            href: `/sites/${siteId}/coupon_codes`,
            icon: <IconTicket stroke={1.5} size={24} />,
          },
        ]
      : []),
    ...(hasPlatformFeature("analytics")
      ? [
          {
            label: (
              <FormattedMessage
                id="main.sidebar.analytics"
                defaultMessage="Analytics"
              />
            ),
            href: `/sites/${siteId}/analytics`,
            icon: <IconFileAnalytics stroke={1.5} size={24} />,
          },
        ]
      : []),
  ];

  const isSubMenuActive = (subMenu: { href: string }[]) => {
    const currentFullPath = location.pathname + location.search;
    return subMenu.some((subItem) => subItem.href === currentFullPath);
  };

  const isSubItemActive = (href: string) => {
    const currentPath = location.pathname;
    const currentParams = new URLSearchParams(location.search);

    const [hrefPath, hrefQuery] = href.split("?");
    const hrefParams = new URLSearchParams(hrefQuery || "");

    if (currentPath !== hrefPath) return false;
    if (hrefParams.has("type")) {
      return currentParams.get("type") === hrefParams.get("type");
    }
    
    return currentParams.get("type") == 'all' ? true : !currentParams.get("type");
  };


  return (
    <div className="flex-shrink-0 flex items-center ml-[70px]">
      {navigationItems.map((item, index) => {
        const { hasDropdown, subMenu } = item;

        if (hasDropdown && subMenu) {
          const parentActive = isSubMenuActive(subMenu);

          return (
            <div
              key={index}
              className="relative"
              onMouseEnter={() => setActiveDropdown(index)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <NavLink
                className="cursor-pointer mr-[40px] font-normal py-[18px] flex items-center gap-1"
                to={item.href}
                activeStyle={{
                  color: "var(--color-primary)",
                  borderBottom: "2px solid var(--color-primary)",
                }}
                style={
                  parentActive
                    ? {
                        color: "var(--color-primary)",
                        borderBottom: "2px solid var(--color-primary)",
                      }
                    : undefined
                }
              >
                {item.label}
                <IconChevronDown size={16} stroke={1.5} />
              </NavLink>

              {activeDropdown === index && (
                <div
                  className="absolute top-full left-0 mt-0 bg-white text-secondary rounded-md shadow-lg py-1 z-50"
                  style={{
                    minWidth: "220px",
                    boxShadow: "0px 4px 16px rgba(17, 17, 17, 0.12)",
                  }}
                >
                  {subMenu.map((subItem, subIndex) => {
                    const subActive = isSubItemActive(subItem.href);
                    return (
                      <NavLink
                        key={subIndex}
                        to={subItem.href}
                        className={`flex items-center px-4 py-2 text-[13px] font-normal uppercase transition-colors border-l-2 ${
                          subActive
                            ? "border-primary text-secondary pointer-events-none"
                            : "border-transparent hover:text-primary"
                        }`}
                        onClick={() => setActiveDropdown(null)}
                      >
                        <span
                          className="mr-3"
                          style={subActive ? { color: "var(--color-primary)" } : undefined}
                        >
                          {subItem.icon}
                        </span>
                        <span>{subItem.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        return (
          <NavLink
            key={index}
            className="cursor-pointer mr-[40px] font-normal py-[18px]"
            to={item.href}
            activeStyle={{
              color: "var(--color-primary)",
              borderBottom: "2px solid var(--color-primary)",
            }}
          >
            {item.label}
          </NavLink>
        );
      })}
    </div>
  );
};

export default Navigation;